import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { SocialMediaPlatform } from "@shared/lib/types/influencer";
import { Button } from "@shared/ui/Button";
import SelectInput from "@shared/ui/SelectInput";
import {
  Card,
  Divider,
  Flex,
  Form,
  Image,
  Input,
  List,
  Popconfirm,
  Select,
  Space,
  Typography,
  Upload,
} from "antd";
import { useEffect, useState } from "react";
import useCreateDeliverableConfig from "../../../entities/deliverableConfigurator/lib/hooks/useCreateDeliverableConfig";

const { TextArea } = Input;

export default function DeliverableForm({
  // initialData,
  onClose,
}: {
  // initialData: IDeliverableConfigurator | null;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [dropdownOpen, setDropdownOpen] = useState<{
    open: boolean;
    id: number | null;
    formatFieldId: number | null;
  }>({
    open: false,
    id: null,
    formatFieldId: null,
  });
  const [newSubtypeValue, setNewSubtypeValue] = useState<string>("");
  const [availableValues, setAvailableValues] = useState<string[]>([]);
  const {
    createDeliverableConfig,
    isCreateDeliverableConfigLoading,
    isCreateDeliverableConfigSuccess,
    createDeliverableConfigError,
  } = useCreateDeliverableConfig();

  const [platformImageUrl, setplatformImageUrl] = useState(
    // initialData?.platformImageUrl ||
    ""
  );

  useEffect(() => {
    if (isCreateDeliverableConfigSuccess) {
      ToastService.success("Deliverable configuration created successfully");
      onClose();
    } else if (createDeliverableConfigError) {
      ToastService.error(createDeliverableConfigError?.message);
    }
  }, [isCreateDeliverableConfigSuccess, createDeliverableConfigError]);
  const [platformDropdownOpen, setPlatformDropdownOpen] =
    useState<boolean>(false);

  const handleImageUpload = async (file: File) => {
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "platform-image-url",
        file
      );
      setplatformImageUrl(uploadedUrl);

      form.setFieldValue("platformImageUrl", uploadedUrl);
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    }
  };

  const handleDeleteImage = () => {
    setplatformImageUrl("");
    ToastService.success("Profile picture removed successfully.");
    form.setFieldValue("platformImageUrl", "");
  };

  const onFinish = async (values: any) => {
    await form.validateFields();

    createDeliverableConfig(values);
  };

  const addNewValues = (
    name: string,
    formatName: number,
    subTypeName: number
  ) => {
    if (!name.trim() || availableValues.includes(name)) {
      return;
    }

    const updatedValues = [...availableValues, name];
    const formVals =
      form.getFieldValue([
        "deliverables",
        formatName,
        "subTypes",
        subTypeName,
        "values",
      ]) || [];
    form.setFieldValue(
      ["deliverables", formatName, "subTypes", subTypeName, "values"],
      [...formVals, name]
    );
    setAvailableValues(updatedValues);
    setNewSubtypeValue("");
    setDropdownOpen({
      open: false,
      id: null,
      formatFieldId: null,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => onFinish(values)}
      initialValues={
        // initialData
        //   ? {
        //       ...initialData,
        //       platformImageUrl: platformImageUrl,
        //     }
        //   :
        {
          deliverables: [
            {
              type: "",
              description: "",
              subTypes: [
                {
                  name: "",
                  description: "",
                  values: [],
                },
              ],
            },
          ],
        }
      }
    >
      <Form.Item
        name="platform"
        label="Platform"
        rules={[{ required: true, message: "Select platform" }]}
      >
        <SelectInput
          placeholder="Select a platform"
          onchange={(value) => form.setFieldValue("platform", value)}
          options={[
            ...(form.getFieldValue("platform") &&
            !Object.values(SocialMediaPlatform).includes(
              form.getFieldValue("platform")
            )
              ? [
                  {
                    label: form.getFieldValue("platform"),
                    value: form.getFieldValue("platform"),
                  },
                ]
              : []),
            ...Object.values(SocialMediaPlatform).map((v) => ({
              label: v,
              value: v,
            })),
          ]}
          dropdownOpen={platformDropdownOpen}
          setDropdownOpen={setPlatformDropdownOpen}
          allowClear
          showSearch
          isFetching={false}
          onSearch={() => {}}
          newItem={{
            placeholder: "Enter a new platform",
            addNewItem: (value) => {
              form.setFieldValue("platform", value);
            },
            addButtonText: "Add platform",
          }}
          value={form.getFieldValue("platform")}
        />

        {/* <Select
          options={Object.values(SocialMediaPlatform).map((v) => ({
            label: v,
            value: v,
          }))}
          placeholder="Select platform"
        /> */}
      </Form.Item>

      <Form.Item
        label="Logo"
        name="platformImageUrl"
        rules={[{ required: true, message: "Logo is required" }]}
      >
        <Upload
          onChange={(info) =>
            info.file && handleImageUpload(info.file.originFileObj!)
          }
          showUploadList={false}
          accept="image/jpeg,image/jpg,image/png"
          beforeUpload={(file) => {
            const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(
              file.type
            );
            if (!isImage) {
              ToastService.error(
                "You can only upload JPG, JPEG, or PNG files!"
              );
            }
            return isImage; // Prevents upload if not a valid image
          }}
        >
          <Button> Upload</Button>
        </Upload>
        {platformImageUrl && (
          <div style={{ marginTop: "16px" }}>
            <Image
              src={platformImageUrl}
              alt="Profile"
              width={60}
              height={60}
            />
            <Popconfirm
              title="Are you sure to delete this image?"
              onConfirm={handleDeleteImage}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger>
                Delete Image
              </Button>
            </Popconfirm>
          </div>
        )}
      </Form.Item>

      <Form.List
        name="deliverables"
        rules={[
          {
            validator: (_, deliverables) => {
              if (!deliverables || deliverables.length < 1) {
                return Promise.reject(
                  new Error(
                    "Please add at least one format for the deliverable."
                  )
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        {(
          formatFields,
          { add: addFormat, remove: removeFormat },
          { errors }
        ) => (
          <>
            <Flex justify="space-between" style={{ marginBottom: 20 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Formats
              </Typography.Title>
              <Form.Item noStyle>
                <Button
                  type="dashed"
                  onClick={() => {
                    const index = formatFields.length;
                    addFormat({
                      type: "",
                      description: "",
                      subTypes: [
                        {
                          name: "",
                          description: "",
                          values: [],
                        },
                      ],
                    });

                    setTimeout(() => {
                      form.scrollToField(["deliverables", index, "category"], {
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Format
                </Button>
              </Form.Item>
            </Flex>
            <List
              dataSource={formatFields}
              style={{ maxHeight: 400, overflow: "auto" }}
              renderItem={(formatField) => (
                <List.Item key={formatField.name}>
                  <Card
                    key={formatField.key}
                    size="small"
                    style={{ marginBottom: 16, width: "100%" }}
                    type="inner"
                    title={`Format ${formatField.name + 1}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeFormat(formatField.name)}
                      />
                    }
                  >
                    <Form.Item
                      name={[formatField.name, "category"]}
                      label="Format Name"
                      rules={[{ required: true, message: "Enter format name" }]}
                    >
                      <Input placeholder="e.g. Story, Feed, Short" />
                    </Form.Item>

                    <Form.Item
                      name={[formatField.name, "description"]}
                      label="Format Description"
                    >
                      <TextArea rows={2} placeholder="Describe the format" />
                    </Form.Item>

                    {/* Nested SubTypes Form.List */}
                    <Form.List name={[formatField.name, "subTypes"]}>
                      {(subFields, { add: addSub, remove: removeSub }) => (
                        <>
                          <Flex justify="space-between" align="center">
                            <Typography.Title level={5} style={{ margin: 0 }}>
                              SubTypes
                            </Typography.Title>
                            <Form.Item noStyle>
                              <Button
                                type="dashed"
                                onClick={() => {
                                  const index = subFields.length;
                                  addSub({
                                    name: "",
                                    description: "",
                                    values: [],
                                  });
                                  setTimeout(() => {
                                    form.scrollToField(
                                      [
                                        "deliverables",
                                        formatField.name,
                                        "subTypes",
                                        index,
                                        "name",
                                      ],
                                      {
                                        behavior: "smooth",
                                        block: "center",
                                      }
                                    );
                                  }, 100);
                                }}
                                icon={<PlusOutlined />}
                              >
                                Add SubType
                              </Button>
                            </Form.Item>
                          </Flex>

                          <List
                            dataSource={subFields}
                            renderItem={(subField) => (
                              <List.Item>
                                <Card
                                  key={subField.key}
                                  size="small"
                                  style={{ marginBottom: 12, width: "100%" }}
                                  type="inner"
                                  title={`SubType ${subField.name + 1}`}
                                  extra={
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => removeSub(subField.name)}
                                    />
                                  }
                                >
                                  <Form.Item
                                    name={[subField.name, "name"]}
                                    label="SubType Name"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Enter subtype name",
                                      },
                                    ]}
                                  >
                                    <Input placeholder="e.g. Static, Video" />
                                  </Form.Item>

                                  <Form.Item
                                    name={[subField.name, "description"]}
                                    label="SubType Description"
                                  >
                                    <TextArea
                                      rows={2}
                                      placeholder="Describe the subtype"
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    name={[subField.name, "values"]}
                                    label="Values"
                                  >
                                    <Select
                                      mode="multiple"
                                      open={
                                        dropdownOpen.id === subField.name &&
                                        dropdownOpen.formatFieldId ===
                                          formatField.name
                                          ? dropdownOpen.open
                                          : false
                                      }
                                      onDropdownVisibleChange={(open) =>
                                        setDropdownOpen((prev) => ({
                                          ...prev,
                                          open: open,
                                          id: subField.name,
                                          formatFieldId: formatField.name,
                                        }))
                                      }
                                      showSearch
                                      placeholder="Select or create a value"
                                      optionFilterProp="children"
                                      allowClear
                                      dropdownRender={(menu) => (
                                        <>
                                          {menu}
                                          <Divider
                                            style={{ margin: "8px 0" }}
                                          />
                                          <Space
                                            style={{
                                              padding: "0 8px 4px",
                                              width: "100%",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <Input
                                              placeholder="Enter new value"
                                              value={newSubtypeValue}
                                              onChange={(e) =>
                                                setNewSubtypeValue(
                                                  e.target.value
                                                )
                                              }
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === "Enter" &&
                                                  newSubtypeValue
                                                ) {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  addNewValues(
                                                    newSubtypeValue,
                                                    formatField?.name,
                                                    subField?.name
                                                  );
                                                }
                                              }}
                                            />
                                            <Button
                                              type="text"
                                              icon={<PlusOutlined />}
                                              disabled={!newSubtypeValue}
                                              onClick={() =>
                                                addNewValues(
                                                  newSubtypeValue,
                                                  formatField?.name,
                                                  subField?.name
                                                )
                                              }
                                            >
                                              Add Value
                                            </Button>
                                          </Space>
                                        </>
                                      )}
                                      filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                          .toLowerCase()
                                          .includes(input.toLowerCase())
                                      }
                                    >
                                      {availableValues.map((role) => (
                                        <Select.Option key={role} value={role}>
                                          {role}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Card>
                              </List.Item>
                            )}
                          />
                        </>
                      )}
                    </Form.List>
                  </Card>
                </List.Item>
              )}
            />
            <Form.Item style={{ margin: 0 }}>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item>
        <Flex gap={10} justify="flex-end">
          <Button
            onClick={() => {
              form.resetFields();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isCreateDeliverableConfigLoading}
          >
            Submit
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );
}
